import type { Dispatch, SetStateAction } from 'react';
import { Check } from '@phosphor-icons/react';

import type { LinkedChild } from '../../hooks/use-roles';
import { Icon } from './icons';

export function RailChildSwitcher({
  linkedChildren,
  activeStudentId,
  open,
  setOpen,
  onSwitch,
}: {
  linkedChildren: LinkedChild[];
  activeStudentId: string | null;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  onSwitch: (id: string) => void;
}) {
  if (linkedChildren.length === 0) return null;
  const activeChild = linkedChildren.find((c) => c.classroomStudentId === activeStudentId) ?? linkedChildren[0]!;

  if (linkedChildren.length <= 1) {
    return (
      <div className="naia-rail__childpick" style={{ padding: '6px 14px 2px', fontSize: 12, color: 'var(--rail-text)' }}>
        Enfant actif
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--rail-text-active)' }}>
          {activeChild.firstName} {activeChild.lastName}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {open && (
        <>
          <div onClick={() => setOpen(false)} className="fixed inset-0 z-[1]" />
          <div role="menu" className="absolute top-[calc(100%+6px)] left-0 right-0 z-[2] bg-[var(--rail-bg)] border border-[var(--rail-line)] rounded-[var(--naia-r-card)] shadow-[0_8px_32px_rgba(0,0,0,0.30)] p-1.5 flex flex-col gap-0.5">
            <div className="text-[9.5px] font-extrabold tracking-[.12em] text-[var(--rail-text-muted)] px-2 py-1 uppercase">CHANGER D&apos;ENFANT</div>
            {linkedChildren.map((c) => {
              const active = c.classroomStudentId === activeChild.classroomStudentId;
              return (
                <button
                  key={c.classroomStudentId}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => { onSwitch(c.classroomStudentId); setOpen(false); }}
                  className={`flex items-center gap-2.5 w-full px-2.5 py-2 min-h-[44px] rounded-[var(--naia-r-sm)] border-none cursor-pointer font-[inherit] text-[13.5px] font-semibold text-left transition-colors${active ? ' bg-[var(--rail-active)] text-[var(--rail-text-active)]' : ' bg-transparent text-[var(--rail-text)] hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)]'}`}
                >
                  <span aria-hidden className="naia-item__icon"><Icon name="student" /></span>
                  <span className="flex-1">{c.firstName} {c.lastName}</span>
                  {active && <Check size={14} weight="bold" aria-hidden className="shrink-0 text-[var(--rail-accent)]" />}
                </button>
              );
            })}
          </div>
        </>
      )}
      <button
        type="button"
        className="naia-roleswitch__toggle flex items-center gap-2.5 w-full px-2.5 py-2 min-h-[44px] rounded-[9px] border border-[var(--rail-line)] bg-[var(--rail-surface)] text-[var(--rail-text)] font-[inherit] text-[13.5px] font-semibold cursor-pointer text-left mb-1 hover:bg-[var(--rail-surface-hover)] hover:text-[var(--rail-text-active)] transition-colors"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span aria-hidden className="naia-item__icon"><Icon name="student" /></span>
        <span className="naia-rail__label flex-1 min-w-0">
          <span className="block text-[9.5px] font-bold text-[var(--rail-text-active)] leading-[1.2]">Enfant actif</span>
          <span className="block whitespace-nowrap overflow-hidden text-ellipsis text-[var(--rail-text-active)]">{activeChild.firstName}</span>
        </span>
        <span aria-hidden className="naia-rail__label shrink-0 opacity-40 text-[var(--rail-text-active)]">⇅</span>
      </button>
    </div>
  );
}
